import { useEffect, useState } from "react";

import {
  EditMode,
  initialCharacterCount,
  parseRichCustomText,
} from "./formatters";

import type { Dispatch, SetStateAction } from "react";
import type { JsonValue } from "../utils/popupCustomText";

export interface CustomTextEditor {
  draftCustomText: string;
  setDraftCustomText: Dispatch<SetStateAction<string>>;
  draftCharacterCount: number;
  setDraftCharacterCount: Dispatch<SetStateAction<number>>;
  editMode: EditMode;
  setEditMode: Dispatch<SetStateAction<EditMode>>;
  /** Whether this field has a custom-text override at all (vs. never offered). */
  customTextSupported: boolean;
  editingCustomText: boolean;
  /** Call from handleSave when editingCustomText is true. */
  saveCustomText: (originalValue: unknown) => void;
}

/**
 * State and save logic for the "Enter Custom Text" side of a popup cell
 * editor, shared by every Quotes-tab editor that supports it.
 */
export function useCustomTextEditor(
  initialCustomText: string | null | undefined,
  onCustomTextChange:
    | ((customText: string | null, originalValue?: JsonValue) => void)
    | undefined,
  stopEditing: (suppressNavigateAfterEdit?: boolean) => void,
): CustomTextEditor {
  const [draftCustomText, setDraftCustomText] = useState<string>(
    initialCustomText ?? "",
  );
  const [draftCharacterCount, setDraftCharacterCount] = useState<number>(
    initialCharacterCount(initialCustomText),
  );
  const [editMode, setEditMode] = useState<EditMode>(
    initialCustomText ? "customText" : "benefit",
  );

  useEffect(() => {
    setDraftCustomText(initialCustomText ?? "");
    setDraftCharacterCount(initialCharacterCount(initialCustomText));
    setEditMode(initialCustomText ? "customText" : "benefit");
  }, [initialCustomText]);

  const customTextSupported =
    initialCustomText !== undefined || onCustomTextChange !== undefined;
  const editingCustomText = customTextSupported && editMode === "customText";

  const saveCustomText = (originalValue: unknown): void => {
    onCustomTextChange?.(
      parseRichCustomText(draftCustomText, draftCharacterCount),
      originalValue as unknown as JsonValue,
    );
    stopEditing(true);
  };

  return {
    draftCustomText,
    setDraftCustomText,
    draftCharacterCount,
    setDraftCharacterCount,
    editMode,
    setEditMode,
    customTextSupported,
    editingCustomText,
    saveCustomText,
  };
}
